import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Creature } from "@dndModels";
import { SourceBookType } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import CheckBox from "@components/input/CheckBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import CommonUtilities from '@utilities/common_utilities';

interface Props {
  initial_filter?: any;
  on_save?: () => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

const rows_per_page = 25;

type SortOption = "Name" | "CR" | "Creature Type" | "Size";

export default function CreatureManager({
  initial_filter,
  on_save
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatures, setCreatures] = useState<Creature[]>([]);
  const [filtered_creatures, setFilteredCreatures] = useState<Creature[]>([]);
  const [selected_creatures, setSelectedCreatures] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<any>(initial_filter || {});
  
  const [source_book_type, setSourceBookType] = useState<SourceBookType | "">("");
  const [source_book_id, setSourceBookId] = useState("");
  
  const [sort_by, setSortBy] = useState<SortOption>("Name");
  const [sort_ascending, setSortAscending] = useState(true);
  
  const [page, setPage] = useState(0);

  const sortCreatures = useCallback((creatures_to_sort: Creature[], use_sort_by: SortOption, asc: boolean) => {
    const sorted = [...creatures_to_sort];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (use_sort_by) {
        case "Name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "CR":
          comparison = a.challenge_rating - b.challenge_rating;
          // Secondary sort by name if CR is equal
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
        case "Creature Type":
          comparison = a.creature_type.localeCompare(b.creature_type);
          // Secondary sort by name if types are equal
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
        case "Size":
          comparison = a.size_num - b.size_num;
          // Secondary sort by name if sizes are equal
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
      }
      
      return asc ? comparison : -comparison;
    });
    
    return sorted;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const creature_objects = await api.getObjects({ data_type: "creature" }) as Creature[];
      setCreatures(creature_objects);
      
      // Apply initial filter if provided
      let filtered: Creature[];
      if (initial_filter && Object.keys(initial_filter).length > 0) {
        filtered = api.filter("creature", creature_objects, initial_filter);
      } else {
        filtered = creature_objects;
      }
      
      // Apply sorting
      const sorted = sortCreatures(filtered, sort_by, sort_ascending);
      setFilteredCreatures(sorted);
    } catch (error) {
      console.error('Error loading creatures:', error);
    }
    setLoading(false);
  }, [initial_filter, sortCreatures, sort_by, sort_ascending]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  const handleFilterChange = useCallback((new_filters: any) => {
    setFilters(new_filters);
    const filtered = api.filter("creature", creatures, new_filters);
    const sorted = sortCreatures(filtered, sort_by, sort_ascending);
    setFilteredCreatures(sorted);
    setPage(0);
  }, [creatures, sort_by, sort_ascending, sortCreatures]);

  const handleSortChange = useCallback((new_sort: SortOption) => {
    setSortBy(new_sort);
    const sorted = sortCreatures(filtered_creatures, new_sort, sort_ascending);
    setFilteredCreatures(sorted);
    setPage(0);
  }, [filtered_creatures, sort_ascending, sortCreatures]);

  const handleToggleSortDirection = useCallback(() => {
    const asc = !sort_ascending;
    setSortAscending(asc);
    const sorted = sortCreatures(filtered_creatures, sort_by, asc);
    setFilteredCreatures(sorted);
    setPage(0);
  }, [filtered_creatures, sort_by, sort_ascending, sortCreatures]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const new_selected = new Set<string>(
        filtered_creatures
          .map(creature => creature._id)
      );
      setSelectedCreatures(prev => {
        const combined = new Set(prev);
        new_selected.forEach(id => combined.add(id));
        return combined;
      });
    } else {
      setSelectedCreatures(new Set());
    }
  };

  const handleSelectAllOnPage = (checked: boolean) => {
    if (checked) {
      const new_selected = new Set<string>(
        filtered_creatures
          .slice(page * rows_per_page, page * rows_per_page + rows_per_page)
          .map(creature => creature._id)
      );
      setSelectedCreatures(prev => {
        const combined = new Set(prev);
        new_selected.forEach(id => combined.add(id));
        return combined;
      });
    } else {
      const page_ids = new Set(
        filtered_creatures
          .slice(page * rows_per_page, page * rows_per_page + rows_per_page)
          .map(creature => creature._id)
      );
      setSelectedCreatures(prev => {
        const filtered = new Set<string>();
        prev.forEach(id => {
          if (!page_ids.has(id)) {
            filtered.add(id);
          }
        });
        return filtered;
      });
    }
  };

  const handleSelectCreature = (creature_id: string) => {
    setSelectedCreatures(prev => {
      const new_set = new Set(prev);
      if (new_set.has(creature_id)) {
        new_set.delete(creature_id);
      } else {
        new_set.add(creature_id);
      }
      return new_set;
    });
  };

  const handleAssignSourceBook = async () => {
    if (selected_creatures.size === 0) {
      alert('Please select at least one creature');
      return;
    }
    
    if (source_book_id === "") {
      alert('Please select a source book to assign to');
      return;
    }

    setSaving(true);
    try {
      const creature_ids = Array.from(selected_creatures);
      const selected_creature_objects = creatures.filter(c => creature_ids.includes(c._id));
      
      // Add new source books to existing ones
      const new_source_book = {
        type: source_book_type === "" ? "Source Book" : source_book_type,
        id: source_book_id,
        third_party: false
      };

      for (const creature of selected_creature_objects) {
        const existing_ids = creature.source_book_ids;
        if (!existing_ids.includes(source_book_id)) {
          // Check if it already has a variant in the source book.
          const variant = creature.variant_group.find(o => o.source_book_ids.includes(source_book_id));
          
          if (!variant) {
            creature.source_books_old.push(new_source_book);
          
            await api.updateObject("creature", creature);
          }
        }
      }
      
      // Reload creatures to reflect changes
      await load();
      setSelectedCreatures(new Set());
      alert(`Successfully assigned ${creature_ids.length} creature(s) to source book`);
      
      if (on_save) {
        on_save();
      }
    } catch (error) {
      console.error('Error assigning source book:', error);
      alert('Error assigning source book');
    }
    setSaving(false);
  };

  const handleNonVersionVariants = async () => {
    if (selected_creatures.size === 0) {
      alert('Please select at least one creature');
      return;
    }

    setSaving(true);
    try {
      const creature_ids = Array.from(selected_creatures);
      const selected_creature_objects = creatures.filter(c => creature_ids.includes(c._id));
      
      let cleaned = 0;
      // for (const creature of selected_creature_objects) {
      //   if (creature.non_version_variant) {
      //     creature.variant_of_id = "";
      //     creature.variant_ids = [];
      //     creature.variant_group = [];
      //     // creature.non_version_variant = false;
      //     await api.updateObject("creature", creature);
      //     cleaned++;
      //   }
      // }
      
      // Reload creatures to reflect changes
      await load();
      setSelectedCreatures(new Set());
      alert(`Successfully cleaned Non-version Variant of ${cleaned} creature(s)`);
      
      if (on_save) {
        on_save();
      }
    } catch (error) {
      console.error('Error cleaning Non-version Variant:', error);
      alert('Error cleaning Non-version Variant');
    }
    setSaving(false);
  };

  const isSelected = (creature_id: string) => selected_creatures.has(creature_id);

  const current_page_creatures = filtered_creatures.slice(
    page * rows_per_page,
    page * rows_per_page + rows_per_page
  );

  const all_page_selected = current_page_creatures.length > 0 && 
    current_page_creatures.every(creature => selected_creatures.has(creature._id));

  if (loading || !fetched) {
    return <LoadingBox />;
  }

  return (
    <Grid container spacing={1}>
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Creature Source Book Manager
      </Grid>
      {/* Filter Section */}
      <Grid
        size={{
          xs: 12
        }}>
        <FilterBox
          data_type="creature"
          the_filter={filters}
          dont_use_api
          all_objects={creatures}
          onChange={handleFilterChange}
        />
      </Grid>
      {/* Sort Section */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Sort Options
      </Grid>
      <Grid
        size={{
          xs: 8
        }}>
        <SelectStringBox
          name="Sort By"
          value={sort_by}
          options={["Name", "CR", "Creature Type", "Size"]}
          onChange={handleSortChange}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <ButtonBox
          name={sort_ascending ? "Ascending ↑" : "Descending ↓"}
          onClick={handleToggleSortDirection}
        />
      </Grid>
      {/* Source Book Selection */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Assign to Source Book
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <SelectSourceBookBox
          type={source_book_type}
          id={source_book_id}
          onChange={(type, id) => {
            setSourceBookType(type);
            setSourceBookId(id);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <ButtonBox
          name={`Assign ${selected_creatures.size} Selected Creature(s)`}
          disabled={
            selected_creatures.size === 0 || 
            source_book_id === "" ||
            saving
          }
          onClick={handleAssignSourceBook}
        />
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        <ButtonBox
          name={`Clean Non-version variants for ${selected_creatures.size} Selected`}
          disabled={selected_creatures.size === 0 || saving}
          onClick={handleNonVersionVariants}
        />
      </Grid>
      {/* Creature List */}
      <Grid
        className="header"
        size={{
          xs: 12
        }}>
        Creatures ({filtered_creatures.length} total, {selected_creatures.size} selected)
      </Grid>
      {/* Select All Checkbox */}
      <Grid
        size={{
          xs: 12
        }}>
        <CheckBox
          name="Select ALL"
          value={selected_creatures.size === filtered_creatures.length && filtered_creatures.length > 0}
          onChange={handleSelectAll}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <CheckBox
          name="Select All on Page"
          value={all_page_selected}
          onChange={handleSelectAllOnPage}
        />
      </Grid>
      {/* Creature Rows */}
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={0} direction="row" className="grid-striped">
          {current_page_creatures.map((creature) => {
            const is_item_selected = isSelected(creature._id);
            const detail = creature.detailFormat(); // creature.detail_format; // char_util.getStringWithReplacementsWithObject({ 
            //   clean_me: creature.detail_format, 
            //   backup: "", 
            //   obj: creature 
            // });
            return (
              <Grid
                key={creature._id}
                className="padded"
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row">
                  <Grid
                    className="centered"
                    size={{
                      xs: 1
                    }}>
                    <Checkbox 
                      checked={is_item_selected}
                      onChange={() => handleSelectCreature(creature._id)}
                    />
                  </Grid>
                  <Grid
                    className={is_item_selected ? "clickable selected" : "clickable"}
                    onClick={() => handleSelectCreature(creature._id)}
                    size={{
                      xs: 11
                    }}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        className="header"
                        size={{
                          xs: 12
                        }}>
                        {creature.name}
                      </Grid>
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        {creature.creature_type_string}, CR {creature.cr_string}
                      </Grid>
                      { detail.length > 0 && 
                        <Grid
                          className="small_label gray"
                          size={{
                            xs: 12
                          }}>{ detail }</Grid> 
                      }
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      {current_page_creatures.length === 0 && (
        <Grid
          className="centered"
          size={{
            xs: 12
          }}>
          No creatures found
        </Grid>
      )}
      {/* Pagination */}
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={1}>
          <Grid
            size={{
              xs: 2
            }}>
            <ButtonBox
              name="Previous"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            />
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 8
            }}>
            Page {page + 1} of {Math.ceil(filtered_creatures.length / rows_per_page)}
            {' '}(Showing {page * rows_per_page + 1}-{Math.min((page + 1) * rows_per_page, filtered_creatures.length)} of {filtered_creatures.length})
          </Grid>
          <Grid
            size={{
              xs: 2
            }}>
            <ButtonBox
              name="Next"
              disabled={(page + 1) * rows_per_page >= filtered_creatures.length}
              onClick={() => setPage(page + 1)}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}